import { useState, useRef, useEffect } from 'react';
import { Download, ChevronDown } from 'lucide-react';
import { palette } from '../../styles/tokens';

interface ExportOption {
  label: string;
  onClick: () => void;
  description?: string;
}

interface ExportMenuProps {
  options: ExportOption[];
}

export function ExportMenu({ options }: ExportMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  if (options.length === 1) {
    // Single option: just a button
    return (
      <button onClick={options[0].onClick} style={{
        display: 'flex', alignItems: 'center', gap: 5,
        padding: '6px 14px', borderRadius: 6, border: `1px solid ${palette.g200}`,
        background: palette.white, cursor: 'pointer', fontSize: 12.5, fontWeight: 500,
        color: palette.g600, fontFamily: 'inherit',
      }}>
        <Download size={13} /> {options[0].label}
      </button>
    );
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(!open)} style={{
        display: 'flex', alignItems: 'center', gap: 5,
        padding: '6px 14px', borderRadius: 6, border: `1px solid ${palette.g200}`,
        background: palette.white, cursor: 'pointer', fontSize: 12.5, fontWeight: 500,
        color: palette.g600, fontFamily: 'inherit',
      }}>
        <Download size={13} /> Export <ChevronDown size={13} />
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 4,
          minWidth: 240, background: palette.white, borderRadius: 8,
          border: `1px solid ${palette.g150}`,
          boxShadow: '0 8px 32px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)',
          zIndex: 20, overflow: 'hidden',
        }}>
          {options.map((opt, i) => (
            <div
              key={i}
              onClick={() => { opt.onClick(); setOpen(false); }}
              style={{
                padding: '10px 14px', cursor: 'pointer',
                borderBottom: i < options.length - 1 ? `1px solid ${palette.g100}` : 'none',
                transition: 'background 0.08s',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = palette.g50)}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <div style={{ fontSize: 13, fontWeight: 500, color: palette.g800 }}>{opt.label}</div>
              {opt.description && (
                <div style={{ fontSize: 11, color: palette.g400, marginTop: 1 }}>{opt.description}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
